/**
 * Rule Detail Handlers
 *
 * Pure command handlers for rule data, draft, save flow,
 * and loading state management.
 */

import { CommandHandlerMap } from '@commandDrivenStateEngine/core/types';
import { RuleDetailState, initialState } from './state';
import { RuleDetailCommand } from './commands';

type ExtractCommand<T extends string> = Extract<RuleDetailCommand, { type: T }>;

// =============================================================================
// DATA HANDLERS
// =============================================================================

const handleSetRule = (
  state: RuleDetailState,
  cmd: ExtractCommand<'automation/ruleDetail/data/setRule'>
): RuleDetailState => ({
  ...state,
  rule: cmd.payload.rule,
  loading: { ...state.loading, rule: false }
});

const handleSetDefinition = (
  state: RuleDetailState,
  cmd: ExtractCommand<'automation/ruleDetail/data/setDefinition'>
): RuleDetailState => ({
  ...state,
  definition: cmd.payload.definition
});

const handleSetDraftRule = (
  state: RuleDetailState,
  cmd: ExtractCommand<'automation/ruleDetail/data/setDraftRule'>
): RuleDetailState => ({
  ...state,
  draftRule: cmd.payload.draftRule,
  loading: { ...state.loading, draft: false }
});

const handleClearAll = (
  _state: RuleDetailState,
  _cmd: ExtractCommand<'automation/ruleDetail/data/clearAll'>
): RuleDetailState => initialState;

// =============================================================================
// SAVE HANDLERS
// =============================================================================

const handleSaveSubmit = (
  state: RuleDetailState,
  _cmd: ExtractCommand<'automation/ruleDetail/save/submit'>
): RuleDetailState => ({
  ...state,
  loading: { ...state.loading, saving: true }
});

const handleSaveCancel = (
  state: RuleDetailState,
  _cmd: ExtractCommand<'automation/ruleDetail/save/cancel'>
): RuleDetailState => state;

// =============================================================================
// LOADING HANDLERS
// =============================================================================

const handleSetRuleLoading = (
  state: RuleDetailState,
  cmd: ExtractCommand<'automation/ruleDetail/loading/setRuleLoading'>
): RuleDetailState => ({
  ...state,
  loading: { ...state.loading, rule: cmd.payload.isLoading }
});

const handleSetDraftLoading = (
  state: RuleDetailState,
  cmd: ExtractCommand<'automation/ruleDetail/loading/setDraftLoading'>
): RuleDetailState => ({
  ...state,
  loading: { ...state.loading, draft: cmd.payload.isLoading }
});

const handleSetSavingLoading = (
  state: RuleDetailState,
  cmd: ExtractCommand<'automation/ruleDetail/loading/setSavingLoading'>
): RuleDetailState => ({
  ...state,
  loading: { ...state.loading, saving: cmd.payload.isLoading }
});

// =============================================================================
// HANDLER MAP
// =============================================================================

export const handlers: CommandHandlerMap<RuleDetailState, RuleDetailCommand> = {
  // Data
  'automation/ruleDetail/data/setRule': handleSetRule,
  'automation/ruleDetail/data/setDefinition': handleSetDefinition,
  'automation/ruleDetail/data/setDraftRule': handleSetDraftRule,
  'automation/ruleDetail/data/clearAll': handleClearAll,
  // Save
  'automation/ruleDetail/save/submit': handleSaveSubmit,
  'automation/ruleDetail/save/cancel': handleSaveCancel,
  // Loading
  'automation/ruleDetail/loading/setRuleLoading': handleSetRuleLoading,
  'automation/ruleDetail/loading/setDraftLoading': handleSetDraftLoading,
  'automation/ruleDetail/loading/setSavingLoading': handleSetSavingLoading
};
